
/**
 * @version 1.1.0
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @copyright Copyright (C) 2021, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 * CHANGELOG:
 *
 * 2021-11-22 (1.0.0) (jhoward)
 *  - Refactor from vanilla JS.
 *
 * 2021-11-29 (1.0.1) (jhoward)
 *  - Support "type" attribute.
 *
 * 2023-01-16 (1.1.0) (jhoward)
 *  - <material-button> now supports type=router-link
 */

import {defineComponent} from "vue";

export default defineComponent({
  name: "material-button",
  props: {
    label: {
      required: true
    },
    type: {
      default: ''
    },
    icon: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    /**
     * @values raised, outline, text, submit
     */
    mod: {
      type: String,
      default: 'raised',
      validator: (value: string) => ['raised', 'outline', 'text', 'submit'].indexOf(value) !== -1
    },
    href: {
      type: String,
      default: '#'
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      active: false,
      mousedown: false,
      animating: false,
      deactivate: false,
      clickTimer: -1,

      ripplePositionX: 0,
      ripplePositionY: 0,
      rippleSize: 0,
      rippleScale: 1,
    };
  },
  computed: {
    containerClasses() {
      return {
        'button--container-raised': this.mod === 'raised' || this.mod === 'submit',
        'button--container-submit': this.mod === 'submit',
        'button--container-outline': this.mod === 'outline',
        'button--container-icon': this.icon,
        'button--container-disabled': this.disabled,
        'button--container-deactivate': this.deactivate,
        'button--container-activate': this.active,
        'button--container-error': this.error,
        'button--container-full_width': this.fullWidth,
        'button--container-loading': this.isLoading
      };
    },
    containerStyles: function () {
      return {
        '--ripple-pos-x': this.ripplePositionX + 'px',
        '--ripple-pos-y': this.ripplePositionY + 'px',
        '--ripple-size': this.rippleSize + 'px',
        '--ripple-scale': this.rippleScale,
        '--button-width': (this.isLoading) ? this.$el.clientWidth + 'px' : null
      };
    },
  },
  methods: {
    buttonRippleAnimationEndHandler: function (event: AnimationEvent) {
      //make sure the mouse button isn't still down
      if (this.mousedown) {
        this.animating = false;
      } else {
        if ('button-ripple-scale-in' === event.animationName) {
          //need to start the deactivate process
          this.deactivate = true;
        } else if ('button-ripple-opacity-out' === event.animationName) {
          //effect animation is complete, let's clean-up
          this.active = false;
          this.deactivate = false;
          this.animating = false;
        }
      }
    },

    buttonMouseUpHandler: function () {
      //don't run the effect for disabled buttons
      if (!this.disabled) {
        this.mousedown = false;

        if (!this.animating) {
          //effect animation is complete, let's clean-up
          this.active = false;
          this.deactivate = true;
        }
      }
    },

    buttonMouseDownHandler: function (event: MouseEvent) {
      //don't run the effect for disabled buttons
      if (!this.disabled) {
        this.mousedown = true;

        clearTimeout(this.clickTimer);

        //reset CSS for button
        this.active = false;
        this.deactivate = false;

        //let the CSS reset settle
        this.clickTimer = window.setTimeout(() => {
          //update CSS properties needed for the effect
          if (this.icon) {
            this.ripplePositionX = Math.round(this.$el.clientWidth / 2);
            this.ripplePositionY = Math.round(this.$el.clientHeight / 2);
            this.rippleSize = Math.max(this.$el.clientWidth, this.$el.clientHeight);
            this.rippleScale = 1;
          } else {
            this.ripplePositionX = event.offsetX;
            this.ripplePositionY = event.offsetY;
            this.rippleSize = (Math.max(this.$el.clientWidth, this.$el.clientHeight) / 1.2);
          }

          //kick off the animation!
          this.active = true;
          this.animating = true;

        }, 16);
      }
    },

    mouseLeaveHandler: function () {
      this.mousedown = false;
      this.active = false;
      this.deactivate = false;
    },

    clickHandler: function (event: Event) {
      if (this.disabled) {
        event.preventDefault();
      }
    },
  }
})
